import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { Points } from 'components/Points/Points';
import { useCalculatePriceMutation } from 'store/services/trade/tradePriceApi';
import { AssetUrls } from 'config/assets';
import { FoilCardImage } from 'components/FoilCardImage';
import './InitiateTradeModal.css';

export interface TradeCard {
  id: string;
  qty: number;
  maxQty: number;  // Maximum quantity the receiver wants
  name: string;
  set: string;  // Current set name (for display)
  setCode: string;  // Current set code (for API calls)
  condition: string;
  language: string;
  foil: boolean;
  edition: string;
  image: string;
  imageSmall?: string;
  value: number;
  baseValue: number;  // Original value before modifications
  foilValue?: number;
  nonFoilValue?: number;
  acceptableSets?: string[];  // Set names the receiver will accept (for display)
  acceptableSetCodes?: string[];  // Set codes the receiver will accept (for API)
  acceptableSetDetails?: Array<{
    name: string;
    code: string;
    cardId?: number;    // Specific card ID for this set version
    imageUrl?: string;  // Image URL for this set version
  }>;  // Combined set info with enhanced data
  wantListId?: number;  // Added for API calls
  availableInFoil?: boolean;  // Whether this card is available in foil
  availableInNonFoil?: boolean;  // Whether this card is available in non-foil
  cardId?: number;  // Track selected card ID for specific version
}

interface Props {
  isOpen: boolean;
  cards: TradeCard[];
  onClose: () => void;
  onInitiateTrade: (cards: TradeCard[]) => void;
  isLoading?: boolean;
  lockId?: string;
  lockExpiresAt?: string;
}

export default function InitiateTradeModal({ isOpen, cards, onClose, onInitiateTrade, isLoading = false, lockId, lockExpiresAt }: Props) {
  const [editableCards, setEditableCards] = useState<TradeCard[]>(cards);
  const [timeRemaining, setTimeRemaining] = useState<number>(300); // 5 minutes in seconds
  const [calculatePrice, { isLoading: isCalculatingPrice }] = useCalculatePriceMutation();

  // Sync cards when props change (when modal opens with new cards)
  useEffect(() => {
    if (isOpen && cards.length > 0) {
      setEditableCards(cards);
    }
  }, [isOpen, cards]);

  // Initialize timer only when modal opens (not on every render)
  useEffect(() => {
    if (isOpen) {
      // Only reset timer when modal is first opened (transition from closed to open)
      if (lockExpiresAt) {
        const expiresAt = new Date(lockExpiresAt).getTime();
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
        setTimeRemaining(remaining);
      } else {
        setTimeRemaining(300); // Default to 5 minutes
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only depend on isOpen, not lockExpiresAt which might change with polling

  // Countdown timer effect
  useEffect(() => {
    if (!isOpen || timeRemaining <= 0) return;

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          onClose(); // Auto-close when timer expires
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isOpen, timeRemaining, onClose]);

  // Format time for display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  // Server-side price calculation - secure against manipulation
  const fetchCardPrices = async (cardsToPrice: TradeCard[]): Promise<Map<number, number>> => {
    const priceMap = new Map<number, number>();
    
    // Filter cards that have wantListId for API call
    const cardsWithWantListId = cardsToPrice.filter(card => card.wantListId);
    
    if (cardsWithWantListId.length === 0) {
      // If no cards have wantListId, return current values as fallback
      cardsToPrice.forEach(card => {
        if (card.wantListId) {
          priceMap.set(card.wantListId, card.value);
        }
      });
      return priceMap;
    }
    
    try {
      const requestPayload = {
        cards: cardsWithWantListId.map(card => ({
          wantListId: card.wantListId!,
          condition: card.condition,
          language: card.language,
          foil: card.foil,
          set: card.set  // Try sending the set name instead of code
        }))
      };
      
      console.log('Sending price calculation request:', requestPayload);
      console.log('Request details:', JSON.stringify(requestPayload, null, 2));
      
      const result = await calculatePrice(requestPayload).unwrap();
      console.log('Backend response:', result);
      console.log('⚠️ Backend price calculation issue detected:');
      console.log('  - Sent set:', requestPayload.cards[0]?.set);
      console.log('  - Received price:', result.cards[0]?.displayPrice);
      console.log('  - Expected prices: The List=17, Mirage=40, 6th Edition=30');
      
      result.cards.forEach(cardPrice => {
        priceMap.set(cardPrice.wantListId, cardPrice.displayPrice);
      });
    } catch (error) {
      console.error('Failed to fetch prices from server:', error);
      // On error, use existing display values
      cardsToPrice.forEach(card => {
        if (card.wantListId) {
          priceMap.set(card.wantListId, card.value);
        }
      });
    }
    
    return priceMap;
  };

  const updateCard = async (cardId: string, updates: Partial<TradeCard>) => {
    const card = editableCards.find(c => c.id === cardId);
    if (!card) return;
    
    // Validate foil changes
    if ('foil' in updates) {
      const requestedFoil = updates.foil;
      if (requestedFoil === true && !card.availableInFoil) {
        toast.warning('This card is only available in non-foil');
        return;
      }
      if (requestedFoil === false && !card.availableInNonFoil) {
        toast.warning('This card is only available in foil');
        return;
      }
    }
    
    const updatedCard = { ...card, ...updates };
    
    // If price-affecting fields changed, get new price from server
    if (('foil' in updates || 'condition' in updates || 'setCode' in updates) && updatedCard.wantListId) {
      console.log('Price update triggered for set change:', {
        cardId,
        updates,
        setCode: updatedCard.setCode,
        set: updatedCard.set,
        wantListId: updatedCard.wantListId
      });
      
      const priceMap = await fetchCardPrices([updatedCard]);
      const newPrice = priceMap.get(updatedCard.wantListId);
      
      console.log('Price response:', {
        oldPrice: card.value,
        newPrice,
        priceMap: Array.from(priceMap.entries())
      });
      
      if (newPrice !== undefined) {
        updatedCard.value = newPrice;
      }
    }
    
    setEditableCards(prev => 
      prev.map(c => c.id === cardId ? updatedCard : c)
    );
  };

  const removeCard = (cardId: string) => {
    setEditableCards(prev => prev.filter(card => card.id !== cardId));
  };

  const incrementQty = (cardId: string) => {
    const card = editableCards.find(c => c.id === cardId)!;
    if (card.qty < card.maxQty) {
      updateCard(cardId, { qty: card.qty + 1 });
    }
  };

  const decrementQty = (cardId: string) => {
    const card = editableCards.find(c => c.id === cardId)!;
    if (card.qty > 1) {
      updateCard(cardId, { qty: card.qty - 1 });
    }
  };

  // Display totals - actual calculation happens server-side
  const totalValue = editableCards.reduce((sum, card) => sum + (card.value * card.qty), 0);
  const totalCards = editableCards.reduce((sum, card) => sum + card.qty, 0);

  const handleInitiateTrade = () => {
    // All price validation happens server-side
    // Frontend prices are for display only
    onInitiateTrade(editableCards);
    // Don't call onClose() here - parent will close on success or handle errors
  };

  const openConditionGuide = () => {
    window.open('/articles/condition-guide', '_blank');
  };

  const openShippingGuide = () => {
    window.open('/articles/shipping', '_blank');
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="initiate-trade-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Finalize Your Trade Package</h2>
          <div className="timer-display" style={{ 
            color: timeRemaining < 60 ? '#ff6b6b' : '#999',
            fontSize: '14px',
            fontWeight: 'bold',
            marginLeft: 'auto',
            marginRight: '10px'
          }}>
            Time remaining: {formatTime(timeRemaining)}
          </div>
          <button className="close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="modal-content">
          <div className="package-summary">
            <div className="summary-item">
              <span className="summary-label">Total Cards:</span>
              <span className="summary-value">{totalCards}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Value:</span>
              <span className="summary-value">{totalValue.toLocaleString()} points</span>
            </div>
          </div>

          <div className="guide-links">
            <button className="guide-link" onClick={openConditionGuide}>
              Condition Guide
            </button>
            <button className="guide-link" onClick={openShippingGuide}>
              Shipping Guide
            </button>
          </div>

          <div className="cards-table">
            <div className="cards-table-wrapper">
              <div className="table-header">
                <div className="col-qty">Qty</div>
                <div className="col-name">Name</div>
                <div className="col-set">Set</div>
                <div className="col-foil">Foil</div>
                <div className="col-condition">Condition</div>
                <div className="col-language">Language</div>
                <div className="col-value">Value</div>
                <div className="col-remove"></div>
              </div>
              
              {editableCards.map((card) => (
                <div key={card.id} className="table-row">
                <div className="col-qty">
                  <div className="qty-controls">
                    <button 
                      className="qty-btn"
                      onClick={() => decrementQty(card.id)}
                      disabled={card.qty <= 1}
                    >
                      -
                    </button>
                    <span className="qty-value">{card.qty}/{card.maxQty}</span>
                    <button 
                      className="qty-btn"
                      onClick={() => incrementQty(card.id)}
                      disabled={card.qty >= card.maxQty}
                      title={card.qty >= card.maxQty ? `Receiver only wants ${card.maxQty}` : ''}
                    >
                      +
                    </button>
                  </div>
                </div>
                
                <div className="col-name">
                  {(card.imageSmall || card.image) ? (
                    <FoilCardImage
                      src={card.imageSmall || card.image}
                      alt={card.name}
                      className="card-thumbnail"
                      foil={card.foil}
                      borderRadius="4px"
                    />
                  ) : (
                    <div className="card-thumbnail card-thumbnail-placeholder" />
                  )}
                  <span>{card.name}</span>
                </div>
                
                <div className="col-set">
                  {(() => {
                    // Determine if we should show dropdown - backward compatible logic
                    const hasMultipleSetDetails = card.acceptableSetDetails && card.acceptableSetDetails.length > 1;
                    const hasMultipleSets = !card.acceptableSetDetails && card.acceptableSets && card.acceptableSets.length > 1;
                    const shouldShowDropdown = hasMultipleSetDetails || hasMultipleSets;

                    // Get set options from either acceptableSetDetails or acceptableSets
                    const setOptions = card.acceptableSetDetails ||
                      (card.acceptableSets?.map((name, index) => ({
                        name: name,
                        code: card.acceptableSetCodes?.[index] || name
                      })) || []);

                    if (shouldShowDropdown) {
                      return (
                        <select
                          value={card.setCode}
                          onChange={(e) => {
                            const selectedSetDetail = setOptions.find(s => s.code === e.target.value);
                            if (selectedSetDetail) {
                              const updates: Partial<TradeCard> = {
                                setCode: selectedSetDetail.code,
                                set: selectedSetDetail.name
                              };

                              // If the new structure includes cardId, use it
                              if ('cardId' in selectedSetDetail) {
                                const cardId = (selectedSetDetail as any).cardId;
                                if (cardId) {
                                  updates.cardId = cardId;
                                }
                              }

                              // If the new structure includes imageUrl, use it
                              if ('imageUrl' in selectedSetDetail) {
                                const imageUrl = (selectedSetDetail as any).imageUrl;
                                if (imageUrl) {
                                  updates.image = imageUrl;
                                  updates.imageSmall = imageUrl
                                    ?.replace('/large/', '/small/')
                                    ?.replace('/normal/', '/small/');
                                }
                              }

                              updateCard(card.id, updates);
                            }
                          }}
                          className="set-select"
                        >
                          {setOptions.map(setDetail => (
                            <option key={setDetail.code} value={setDetail.code}>
                              {setDetail.name}
                            </option>
                          ))}
                        </select>
                      );
                    } else {
                      return <span>{card.set}</span>;
                    }
                  })()}
                </div>
                
                <div className="col-foil">
                  {card.availableInFoil && card.availableInNonFoil ? (
                    <select 
                      value={card.foil ? 'foil' : 'non-foil'}
                      onChange={(e) => updateCard(card.id, { foil: e.target.value === 'foil' })}
                      className="foil-select"
                    >
                      <option value="non-foil">Non-Foil</option>
                      <option value="foil">Foil</option>
                    </select>
                  ) : (
                    <span className="foil-text">
                      {card.availableInFoil ? 'Foil' : 'Non-Foil'}
                    </span>
                  )}
                </div>
                
                <div className="col-condition">
                  <select 
                    value={card.condition}
                    onChange={(e) => updateCard(card.id, { condition: e.target.value })}
                    className="condition-select"
                  >
                    <option value="NEAR_MINT">NM</option>
                    <option value="SLIGHTLY_PLAYED">LP</option>
                    <option value="MODERATELY_PLAYED">MP</option>
                  </select>
                </div>
                
                <div className="col-language">
                  <select 
                    value={card.language}
                    onChange={(e) => updateCard(card.id, { language: e.target.value })}
                    className="language-select"
                  >
                    <option value="EN">EN</option>
                    <option value="FR">FR</option>
                    <option value="DE">DE</option>
                    <option value="ES">ES</option>
                    <option value="IT">IT</option>
                    <option value="JP">JP</option>
                  </select>
                </div>
                
                <div className="col-value">
                  <Points points={card.value * card.qty} type="small" />
                </div>
                
                <div className="col-remove">
                  <img
                    src={AssetUrls.icons.dropdowns.close}
                    alt="Remove"
                    className="remove-icon"
                    onClick={() => removeCard(card.id)}
                  />
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <MagicButton
            label="Cancel"
            color="dark-grey"
            onPress={onClose}
            isDisabled={isLoading}
          />
          <MagicButton
            label={isLoading ? "Initiating..." : "Initiate Trade"}
            color="purple"
            onPress={handleInitiateTrade}
            isDisabled={editableCards.length === 0 || isLoading || totalValue <= 0 || isCalculatingPrice}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}